import macDesktopHive from './img/mac-desktop-hive.png';
import macLaptopHive from './img/mac-laptop-hive.png';
import windowsDesktopHive from './img/windows-desktop.png';
import windowsLaptopHive from './img/windows-laptop.png';

import macDesktopIris from './img/mac-desktop.png';
import macLaptopIris from './img/mac-laptop.png';
import windowsDesktopIris from './img/windows-desktop.png';
import windowsLaptopIris from './img/windows-laptop.png';

import { config } from '#~/config/index.js';
import { twMerge } from 'cn';
import { useMemo, type ReactElement } from 'react';

import { broker } from '#~/agent/broker.js';
import {
  type DiscoveredService,
  enhanceServiceDeviceTypeWithUnifiedDetection
} from '#~/agent/discovery/utils.js';
import { useDeviceDetection, convertDeviceInfoToMachineInfo } from '#~/utils/index.js';
import type { Agent } from '@studio/models/agent';
import { MachineType, OperatingSystem } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { useGetMachineAgentId, useRoomAgent } from '../hooks.js';

// Define image mapping types
type DeviceImageMapping = Partial<{
  [key in OperatingSystem]: Partial<{
    [key in MachineType]: string;
  }>;
}>;

// Create image mappings for each vendor
const hiveImages: DeviceImageMapping = {
  [OperatingSystem.OS_WINDOWS]: {
    [MachineType.MACHINE_TYPE_LAPTOP]: windowsLaptopHive,
    [MachineType.MACHINE_TYPE_DESKTOP]: windowsDesktopHive
  },
  [OperatingSystem.OS_MAC]: {
    [MachineType.MACHINE_TYPE_LAPTOP]: macLaptopHive,
    [MachineType.MACHINE_TYPE_DESKTOP]: macDesktopHive
  }
} as const;

const irisImages: DeviceImageMapping = {
  [OperatingSystem.OS_WINDOWS]: {
    [MachineType.MACHINE_TYPE_LAPTOP]: windowsLaptopIris,
    [MachineType.MACHINE_TYPE_DESKTOP]: windowsDesktopIris
  },
  [OperatingSystem.OS_MAC]: {
    [MachineType.MACHINE_TYPE_LAPTOP]: macLaptopIris,
    [MachineType.MACHINE_TYPE_DESKTOP]: macDesktopIris
  }
} as const;

interface DeviceTypeInfo {
  os: OperatingSystem;
  machineType: MachineType;
}

export interface AgentSystemInfo {
  os: OperatingSystem;
  machineType: MachineType;
}

/**
 * Unified device type determination function that works with both DiscoveredService and Agent system info
 * This ensures consistent device type logic across Bridge Discovery and Bridge Tray
 */
const determineDeviceType = (
  source:
    | DiscoveredService
    | { agentId: Agent['id']; systemInfo?: AgentSystemInfo | undefined }
    | null
): DeviceTypeInfo => {
  // Default fallback
  const defaultDeviceType: DeviceTypeInfo = {
    os: OperatingSystem.OS_MAC,
    machineType: MachineType.MACHINE_TYPE_DESKTOP
  };

  if (!source) {
    return defaultDeviceType;
  }

  // Handle Agent system info (Bridge Tray case)
  if ('agentId' in source && source.systemInfo) {
    return {
      os: source.systemInfo.os ?? OperatingSystem.OS_MAC,
      machineType: source.systemInfo.machineType ?? MachineType.MACHINE_TYPE_DESKTOP
    };
  }

  // Handle DiscoveredService (Bridge Discovery case)
  const service = source as DiscoveredService;

  // Use enhanced service device type detection that avoids local device fallback
  const enhancedResult: {
    os: OperatingSystem;
    machineType: MachineType;
    confidence: 'high' | 'medium' | 'low';
  } = enhanceServiceDeviceTypeWithUnifiedDetection(service);
  return {
    os: enhancedResult.os,
    machineType: enhancedResult.machineType
  };
};

/**
 * Get device image using unified device type determination
 * This replaces the old getServiceImage function with more reliable logic
 */
const getUnifiedDeviceImage = (
  source:
    | DiscoveredService
    | { agentId: Agent['id']; systemInfo?: AgentSystemInfo | undefined }
    | null
): string => {
  const { os, machineType } = determineDeviceType(source);
  return getImage(os, machineType);
};

/**
 * Unified device image hook that provides consistent device type detection
 * Always prioritizes mDNS-based detection when available to ensure consistency
 */
export const useDeviceImage = (
  source:
    | DiscoveredService
    | { agentId: Agent['id']; systemInfo?: AgentSystemInfo | undefined }
    | null
): string => {
  // Device detection logic. We won't really use this, since, mDNS and `agentInfo` handle majority of cases but for
  // machines that are local to the studio, we will use this info.
  const deviceInfo = useDeviceDetection();

  return useMemo(() => {
    if (!source) {
      const machineInfo = convertDeviceInfoToMachineInfo(deviceInfo);
      return getImage(machineInfo.os, machineInfo.machineType);
    }

    // Handle DiscoveredService (mDNS) - use enhanced detection without local fallback
    if ('address' in source && 'port' in source && 'data' in source) {
      return getUnifiedDeviceImage(source);
    }

    // Handle Agent; can have systemInfo or not...
    if ('agentId' in source) {
      if (source.systemInfo) {
        // Create a properly typed object for getUnifiedDeviceImage
        const agentWithSystemInfo: { agentId: Agent['id']; systemInfo: AgentSystemInfo } = {
          agentId: source.agentId,
          systemInfo: source.systemInfo
        };

        return getUnifiedDeviceImage(agentWithSystemInfo);
      }

      // Check device info before returning default image
      if (deviceInfo) {
        const machineInfo = convertDeviceInfoToMachineInfo(deviceInfo);
        return getImage(machineInfo.os, machineInfo.machineType);
      }

      // If all else fails, use our generic, default, fallback image of mac and desktop
      return getImage(OperatingSystem.OS_MAC, MachineType.MACHINE_TYPE_DESKTOP);
    }

    // We honestly should never get here, but if we do, return default image. There are bigger problems to worry about
    // if we get here, but we need to return something.
    return getImage(OperatingSystem.OS_MAC, MachineType.MACHINE_TYPE_DESKTOP);
  }, [source, deviceInfo]);
};

// Used exclusively for the electron tray states
export const DeviceImage = ({ className }: { className?: string }): ReactElement => {
  return (
    <img
      src={GetAgentDeviceImage()}
      className={twMerge('mx-auto mt-8 w-[120px]', className)}
      data-stag='bridge.device_image.main'
    />
  );
};

const GetAgentDeviceImage = () => {
  // Machine agent:
  const { agentId: machineAgentId } = useGetMachineAgentId();
  const roomAgent = useRoomAgent(machineAgentId);
  const agentId = roomAgent?.agent.id;
  const proxy = broker.acquire(agentId);

  const agentOS = proxy?.state?.status?.encoder?.os || OperatingSystem.OS_MAC;

  // This will always default to the `desktop` machine type on initial link, since,
  // we haven't connected the bridge/encoder yet.
  const machineType =
    proxy?.state?.status?.encoder?.machineType || MachineType.MACHINE_TYPE_DESKTOP;

  return getImage(agentOS, machineType);
};

export const getImage = (os: OperatingSystem, machineType: MachineType): string => {
  const imageSet = config.vendor === 'ptzoptics' ? hiveImages : irisImages;
  return (
    imageSet[os]?.[machineType] ??
    imageSet[OperatingSystem.OS_MAC]?.[MachineType.MACHINE_TYPE_DESKTOP] ??
    macDesktopIris
  );
};
